import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

/**
 * A question a form asked that we could not answer.
 *
 * Recorded so the user can answer it once, in their own time, and have every
 * later form with the same question fill itself. It is NOT an answer: nothing
 * here is ever typed into an application until the user writes a value for it.
 */
export const pendingQuestionSchema = z.object({
  id: z.string(),
  label: z.string(),
  board: z.string().nullable(),
  jobUrl: z.string().nullable(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
});

export const pendingQuestionListResponseSchema = z.object({
  items: z.array(pendingQuestionSchema),
});

/** One posting can legitimately ask a dozen; 40 is far above any real form. */
export const MAX_PENDING_QUESTIONS_PER_REQUEST = 40;

export const recordPendingQuestionsSchema = z.object({
  questions: z
    .array(z.string().trim().min(1).max(500))
    .min(1)
    .max(MAX_PENDING_QUESTIONS_PER_REQUEST),
  board: z.string().trim().max(60).optional(),
  jobUrl: z.string().trim().max(2048).optional(),
});

export const answerPendingQuestionsSchema = z.object({
  answers: z
    .array(
      z.object({
        label: z.string().trim().min(1).max(500),
        answer: z.string().trim().min(1).max(2000),
      }),
    )
    .min(1)
    .max(50),
});

export class RecordPendingQuestionsDto extends createZodDto(recordPendingQuestionsSchema) {}
export class AnswerPendingQuestionsDto extends createZodDto(answerPendingQuestionsSchema) {}

export type PendingQuestion = z.infer<typeof pendingQuestionSchema>;
export type PendingQuestionListResponse = z.infer<typeof pendingQuestionListResponseSchema>;
/**
 * `savedLabels` is the contract that stops the client discarding a typed
 * answer it never persisted: counts alone cannot say WHICH of a batch reached
 * the bank, so a cap-full or duplicate skip read as success.
 */
export type AnswerPendingQuestionsResponse = {
  created: number;
  skipped: number;
  savedLabels: string[];
};
